import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Autocomplete,
  TextField,
  InputAdornment,
} from "@mui/material";
import { CloudUpload as UploadIcon } from "@mui/icons-material";
import { useFormikContext } from "formik";
import {
  GenericTextField,
  GenericDateField,
  GenericLookupField,
} from "../../../components/common/FormFields";
import {
  ClaimStatus,
  ClaimStatusLabels,
  DriverChargedStatus,
  DriverChargedStatusLabels,
  AutoLBTypeLabels,
  ClaimCompanyDto,
  LookupDto,
} from "../../../types/claim";
import { US_STATES } from "../../../constants/usStates";
import { ClaimFormValues, computeTotalEstimate, parseEstimate } from "../validation/claimFormValidation";
import ClaimPendingFilesSection, { PendingFile } from "./ClaimPendingFilesSection";

interface ClaimFormLayoutProps {
  mode: "create" | "edit";
  claimId?: number;
  availableCompanies?: ClaimCompanyDto[];
  availableDrivers?: LookupDto[];
  availableSafetyUsers?: LookupDto[];
  availableTrucks?: LookupDto[];
  availableTrailers?: LookupDto[];
  pendingFiles?: PendingFile[];
  onPendingFilesChange?: (files: PendingFile[]) => void;
}

const statusOptions = Object.entries(ClaimStatusLabels).map(
  ([value, label]) => ({
    id: Number(value),
    label,
  })
);

const driverChargedOptions = Object.entries(DriverChargedStatusLabels).map(
  ([value, label]) => ({
    id: Number(value),
    label,
  })
);

const autoLBOptions = Object.entries(AutoLBTypeLabels).map(
  ([value, label]) => ({
    id: Number(value),
    label,
  })
);

const ClaimFormLayout: React.FC<ClaimFormLayoutProps> = ({
  mode,
  claimId,
  availableCompanies = [],
  availableDrivers = [],
  availableSafetyUsers = [],
  availableTrucks = [],
  availableTrailers = [],
  pendingFiles = [],
  onPendingFilesChange,
}) => {
  const formik = useFormikContext<ClaimFormValues>();
  const truckValue = parseEstimate(formik.values.truckEstimate);
  const trailerValue = parseEstimate(formik.values.trailerEstimate);
  const hasSplitEstimates = truckValue != null || trailerValue != null;
  const displayTotal = computeTotalEstimate(
    formik.values.truckEstimate,
    formik.values.trailerEstimate,
    formik.values.legacyTotalEstimate
  );
  const isLegacyTotal =
    !hasSplitEstimates && formik.values.legacyTotalEstimate != null;

  const [policeReportTrigger, setPoliceReportTrigger] = useState<{
    open: boolean;
    category: string;
    onClose: () => void;
  }>({ open: false, category: "PoliceReport", onClose: () => {} });

  const companyLookupOptions = availableCompanies.map((c) => ({
    id: c.id,
    name: c.name,
  }));

  return (
    <Box>
      {/* Claim Details */}
      <Card sx={{ mb: 3, mt: 1 }}>
        <CardContent sx={{ pt: 3 }}>
          <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
            Claim Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <GenericDateField
                name="dateOfLoss"
                label="Date of Loss"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GenericTextField
                name="city"
                label="City"
                placeholder="Enter city"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={US_STATES}
                getOptionLabel={(option) => `${option.code} - ${option.name}`}
                isOptionEqualToValue={(option, value) =>
                  option.code === value?.code
                }
                value={
                  US_STATES.find((s) => s.code === formik.values.state) || null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue("state", newValue?.code ?? "");
                }}
                onBlur={() => formik.setFieldTouched("state", true)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="State"
                    placeholder="Select state..."
                    error={
                      formik.touched.state && Boolean(formik.errors.state)
                    }
                    helperText={
                      formik.touched.state &&
                      typeof formik.errors.state === "string"
                        ? formik.errors.state
                        : ""
                    }
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GenericLookupField
                name="claimCompany"
                label="Company"
                options={companyLookupOptions}
                idFieldName="claimCompanyId"
                nameFieldName="claimCompanyName"
                placeholder="Select or type company name"
                allowCreate={true}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={availableSafetyUsers}
                getOptionLabel={(option) => option.name}
                value={
                  availableSafetyUsers.find(
                    (u) => u.id === formik.values.assignedToUserId
                  ) || null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue(
                    "assignedToUserId",
                    newValue?.id ?? null
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Assigned To (Safety)"
                    placeholder="Search safety users..."
                    error={
                      formik.touched.assignedToUserId &&
                      Boolean(formik.errors.assignedToUserId)
                    }
                    helperText={
                      formik.touched.assignedToUserId &&
                      typeof formik.errors.assignedToUserId === "string"
                        ? formik.errors.assignedToUserId
                        : ""
                    }
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={availableDrivers}
                getOptionLabel={(option) => option.name}
                value={
                  availableDrivers.find(
                    (d) => d.id === formik.values.driverEmployeeId
                  ) || null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue(
                    "driverEmployeeId",
                    newValue?.id ?? null
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Driver"
                    placeholder="Search drivers..."
                    error={
                      formik.touched.driverEmployeeId &&
                      Boolean(formik.errors.driverEmployeeId)
                    }
                    helperText={
                      formik.touched.driverEmployeeId &&
                      typeof formik.errors.driverEmployeeId === "string"
                        ? formik.errors.driverEmployeeId
                        : ""
                    }
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={availableTrucks}
                getOptionLabel={(option) => option.name}
                value={
                  availableTrucks.find(
                    (t) => t.id === formik.values.vehicleId
                  ) || null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue("vehicleId", newValue?.id ?? null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Truck"
                    placeholder="Search trucks..."
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={availableTrailers}
                getOptionLabel={(option) => option.name}
                value={
                  availableTrailers.find(
                    (t) => t.id === formik.values.trailerId
                  ) || null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue("trailerId", newValue?.id ?? null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Trailer"
                    placeholder="Search trailers..."
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GenericTextField
                name="claimNumber"
                label="Claim No."
                placeholder="Enter claim number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={autoLBOptions}
                getOptionLabel={(option) => option.label}
                value={
                  formik.values.autoLB != null
                    ? autoLBOptions.find((o) => o.id === formik.values.autoLB) ||
                      null
                    : null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue("autoLB", newValue?.id ?? null);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Auto LB"
                    placeholder="Select Auto LB..."
                    error={
                      formik.touched.autoLB && Boolean(formik.errors.autoLB)
                    }
                    helperText={
                      formik.touched.autoLB &&
                      typeof formik.errors.autoLB === "string"
                        ? formik.errors.autoLB
                        : ""
                    }
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Description & Notes */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            Description & Notes
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <GenericTextField
                name="description"
                label="Description"
                multiline
                rows={4}
                placeholder="Enter claim description..."
              />
            </Grid>
            <Grid item xs={12}>
              <GenericTextField
                name="note"
                label="Note"
                multiline
                rows={4}
                placeholder="Enter notes..."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Adjuster Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            Adjuster Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <GenericTextField
                name="adjusterName"
                label="Adjuster Name"
                placeholder="Enter adjuster name"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <GenericTextField
                name="adjusterPhone"
                label="Adjuster Phone"
                placeholder="Enter adjuster phone"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <GenericTextField
                name="adjusterEmail"
                label="Adjuster Email"
                placeholder="Enter adjuster email"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Police Report & Financials */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            Police Report & Financials
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={9}>
              <GenericTextField
                name="policeReportDescription"
                label="Police Report Description"
                multiline
                rows={2}
                placeholder="Enter police report details..."
              />
            </Grid>
            <Grid item xs={12} sm={3} sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                fullWidth
                onClick={() => {
                  setPoliceReportTrigger({
                    open: true,
                    category: "PoliceReport",
                    onClose: () =>
                      setPoliceReportTrigger((prev) => ({ ...prev, open: false })),
                  });
                }}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Add Police Report Files
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Total Estimate Truck"
                name="truckEstimate"
                type="number"
                value={formik.values.truckEstimate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.truckEstimate &&
                  Boolean(formik.errors.truckEstimate)
                }
                helperText={
                  formik.touched.truckEstimate &&
                  typeof formik.errors.truckEstimate === "string"
                    ? formik.errors.truckEstimate
                    : ""
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: "0.01" }}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Total Estimate Trailer"
                name="trailerEstimate"
                type="number"
                value={formik.values.trailerEstimate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.trailerEstimate &&
                  Boolean(formik.errors.trailerEstimate)
                }
                helperText={
                  formik.touched.trailerEstimate &&
                  typeof formik.errors.trailerEstimate === "string"
                    ? formik.errors.trailerEstimate
                    : ""
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, step: "0.01" }}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Total Estimate"
                name="totalEstimate"
                type="number"
                value={displayTotal ?? ""}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                helperText={
                  isLegacyTotal
                    ? "Legacy total — enter truck/trailer estimates to replace this value"
                    : hasSplitEstimates
                      ? "Sum of truck and trailer estimates"
                      : ""
                }
                inputProps={{ min: 0, step: "0.01" }}
                sx={{
                  mb: 2,
                  "& .MuiInputBase-input": { bgcolor: "action.hover" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={statusOptions}
                getOptionLabel={(option) => option.label}
                value={
                  statusOptions.find(
                    (s) => s.id === formik.values.status
                  ) ?? undefined
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue(
                    "status",
                    newValue?.id ?? ClaimStatus.NotStartedYet
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disableClearable
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Status"
                    required
                    placeholder="Search status..."
                    error={
                      formik.touched.status && Boolean(formik.errors.status)
                    }
                    helperText={
                      formik.touched.status &&
                      typeof formik.errors.status === "string"
                        ? formik.errors.status
                        : ""
                    }
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                size="small"
                options={driverChargedOptions}
                getOptionLabel={(option) => option.label}
                value={
                  formik.values.driverCharged != null
                    ? driverChargedOptions.find(
                        (d) => d.id === formik.values.driverCharged
                      ) || null
                    : null
                }
                onChange={(_, newValue) => {
                  formik.setFieldValue(
                    "driverCharged",
                    newValue?.id ?? null
                  );
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Driver Charged"
                    placeholder="Search..."
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <ClaimPendingFilesSection
            pendingFiles={pendingFiles}
            onChange={onPendingFilesChange ?? (() => {})}
            openDialogWith={policeReportTrigger}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClaimFormLayout;
